/**
 * queue.ts — the module-singleton analytics batching engine (STORY-009-003).
 *
 * A single in-memory queue shared by every `useAnalytics()` consumer. It
 * flushes to `POST /analytics/batch` (STORY-009-002) when the queue reaches
 * 20 events OR 5 s (±jitter) elapse since the timer armed OR the page is
 * hiding (REQ-PRD-§11.8-D). Analytics must never block UI render, so every
 * flush is fire-and-forget and a failure re-queues rather than throws.
 *
 * Wire format — mirrors the SHIPPED endpoint contract (`AnalyticsBatchRequest`
 * in `apps/api/garden/analytics/schemas/batch.py`), NOT the story pseudocode:
 *   { events: [{ event_type, session_id, occurred_at, properties, language? }] }
 *   - up to 20 events per request (MAX_EVENTS_PER_BATCH);
 *   - 202 { accepted } on success; anonymous callers are allowed; an invalid
 *     token degrades to anonymous server-side; 60/min rate-limit per identity.
 *
 * Auth + base URL — reuses the api-client conventions (`@/lib/api-client`,
 * `@/lib/auth-store`):
 *   - the API base URL is resolved exactly the way `api-client` resolves it
 *     (env override → `/api` in dev → `""` in prod/test); we do NOT hardcode
 *     `/api/...`;
 *   - the fetch path attaches `Authorization: Bearer <in-memory access token>`
 *     when present so the server can attribute the batch to a principal;
 *   - the `sendBeacon` (pagehide) path CANNOT carry headers, so those flushes
 *     go ANONYMOUS by construction. This is acceptable: server-side
 *     attribution simply records `actor_id = NULL` for beacon-flushed events.
 *
 * Stub-queue drain (integration with STORY-003-003): marketplace consumers
 * (`trackContactClick` in `@/lib/deep-links`, the browse emitters in
 * `@/features/marketplace/browse-analytics`) push `{ name, ts, props }` events
 * onto `window.__gardenAnalyticsQueue`. EPIC-009 owns the drain side: on every
 * flush we splice that stub queue and map each entry into the batch pipeline
 * (`name → event_type`, `ts → occurred_at` ISO, `props → properties`). The
 * stub module's invariants are preserved — first-party only, no PII, no
 * localStorage; we only READ + drain it and never relax its guardrails.
 *
 * Security / guardrail notes:
 *   - first-party only: the only network target is GardEn's own endpoint.
 *     No third-party tracker is loaded (REQ-PRD-§11.2-C).
 *   - no PII: properties are scrubbed at the `track` boundary (see
 *     `@/lib/analytics/pii`) and again server-side; the stub contract is
 *     PII-free by type.
 *   - no localStorage: the queue is an in-memory array; only the non-auth
 *     session id touches `sessionStorage` (see `@/lib/analytics/session`).
 */
import { getApiClientConfig } from "@/lib/api-client";
import { useAuthStore } from "@/lib/auth-store";
import {
  ANALYTICS_QUEUE_KEY,
  type AnalyticsEvent as StubAnalyticsEvent,
} from "@/lib/analytics-queue";

import { ensureSessionId } from "@/lib/analytics/session";

/** The ingestion endpoint path, appended to the resolved api-client base URL. */
export const ANALYTICS_BATCH_PATH = "/analytics/batch" as const;

/** Flush when the queue reaches this many events (REQ-PRD-§11.8-D + AC#2). */
export const MAX_BATCH_SIZE = 20;

/** Base flush interval in ms (REQ-PRD-§11.8-D + AC#3). */
export const FLUSH_INTERVAL_MS = 5_000;

/**
 * Jitter applied to the timer to avoid herd flushes across many tabs/clients
 * (story Open question default: ±500 ms). Each armed timer fires at
 * `FLUSH_INTERVAL_MS + uniform(-JITTER, +JITTER)`.
 */
export const FLUSH_JITTER_MS = 500;

/**
 * Hard cap on the in-memory queue. On a sustained failure (extended offline)
 * the queue grows as re-queued batches pile up; beyond this we drop the
 * OLDEST events (FIFO eviction, AC#9) so memory can't run away. A dropped
 * event is tolerable — analytics is best-effort (ADR-036).
 */
export const MAX_QUEUE_SIZE = 100;

/**
 * Maps a stub-queue event `name` onto a CATALOGUED `event_type`
 * (`apps/api/garden/analytics/event_catalogue.py`). Only names that differ
 * from their catalogue key need an entry; everything else passes through
 * unchanged. The server drops any `event_type` not in the catalogue (with a
 * log), so an accurate mapping here is what gets a stub event persisted.
 *
 * Known mapping: STORY-003-003's `trackContactClick` enqueues the legacy
 * name `marketplace.contact_click`; the catalogue type is
 * `marketplace.contact.clicked`. We translate it here.
 *
 * NOTE (documented drift, out of scope to "fix"): STORY-003-005's browse
 * emitters enqueue `marketplace.browse.*` and `marketplace.product.card_clicked`,
 * none of which exist in the STORY-009-001 catalogue. They are drained too,
 * pass through unmapped, and are dropped server-side with a log — the
 * documented behaviour for an uncatalogued type. Adding those catalogue rows
 * is a backend concern (a new event-catalogue story), NOT this frontend hook;
 * inventing a client→server mapping to a non-existent type would not persist
 * them either. Left as-is intentionally.
 */
const STUB_NAME_TO_EVENT_TYPE: Readonly<Record<string, string>> = {
  "marketplace.contact_click": "marketplace.contact.clicked",
};

/** One pending event, in the wire shape minus the per-flush `session_id`. */
export interface PendingEvent {
  event_type: string;
  /** ISO-8601 timestamp captured at track time. */
  occurred_at: string;
  properties: Record<string, unknown>;
  language?: string;
}

/** The dev-only window hook for inspecting the queue (AC#10/#11). */
declare global {
  interface Window {
    /**
     * DEV-ONLY mirror of the in-memory analytics queue for console
     * inspection. Assigned only when `import.meta.env.DEV`; `undefined` in
     * production builds (AC#11).
     */
    __GARDEN_ANALYTICS_QUEUE__?: PendingEvent[];
  }
}

/** The shared queue. Module-singleton — NOT per-hook-instance. */
const queue: PendingEvent[] = [];

/** The armed flush timer handle (`window.setTimeout` returns a number). */
let flushTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Expose the live queue array reference on `window` in dev so a developer
 * can inspect pending events (AC#10). Never assigned in prod (AC#11).
 */
function exposeDevHandle(): void {
  if (import.meta.env.DEV && typeof window !== "undefined") {
    window.__GARDEN_ANALYTICS_QUEUE__ = queue;
  }
}
exposeDevHandle();

/** Resolve the absolute analytics endpoint URL via the api-client base. */
function resolveBatchUrl(): string {
  return getApiClientConfig().baseUrl + ANALYTICS_BATCH_PATH;
}

/** Compute the next jittered flush delay (±FLUSH_JITTER_MS). */
function nextFlushDelay(): number {
  const jitter = (Math.random() * 2 - 1) * FLUSH_JITTER_MS;
  return Math.max(0, FLUSH_INTERVAL_MS + jitter);
}

/**
 * Drain the STORY-003-003 stub queue (`window.__gardenAnalyticsQueue`) into
 * the batching pipeline. Splices the stub queue empty and maps each entry:
 *   name → event_type (via STUB_NAME_TO_EVENT_TYPE), ts → occurred_at ISO,
 *   props → properties. PII-free by the stub contract; not re-scrubbed (the
 *   stub type carries only opaque ids + enums, and the server redactor is the
 *   backstop). No-op outside a DOM context.
 */
export function drainStubQueue(): void {
  if (typeof window === "undefined") {
    return;
  }
  const stub = window[ANALYTICS_QUEUE_KEY];
  if (stub === undefined || stub.length === 0) {
    return;
  }
  const drained: StubAnalyticsEvent[] = stub.splice(0, stub.length);
  for (const event of drained) {
    enqueueEvent({
      event_type: STUB_NAME_TO_EVENT_TYPE[event.name] ?? event.name,
      occurred_at: new Date(event.ts).toISOString(),
      properties: { ...event.props },
    });
  }
}

/**
 * Push a fully-formed pending event onto the queue and (re)schedule a flush.
 * The hook's `track` builds the event (redaction + language); the stub drain
 * builds it from a mapped stub entry. Both land here.
 */
export function enqueueEvent(event: PendingEvent): void {
  queue.push(event);
  scheduleFlush();
}

/**
 * Arm or trigger a flush per the batching policy:
 *   - queue at/over MAX_BATCH_SIZE → flush immediately (AC#2);
 *   - otherwise, arm the 5 s (±jitter) timer if not already armed (AC#3).
 */
function scheduleFlush(): void {
  if (queue.length >= MAX_BATCH_SIZE) {
    void flushNow("size");
    return;
  }
  if (flushTimer === null) {
    flushTimer = setTimeout(() => {
      flushTimer = null;
      void flushNow("timer");
    }, nextFlushDelay());
  }
}

/** Clear the armed timer, if any. */
function clearFlushTimer(): void {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

/**
 * Re-queue a failed batch at the FRONT of the queue (preserve order) and
 * enforce the hard cap by dropping the OLDEST events (FIFO eviction, AC#9).
 * Drops are warned in dev. Best-effort: a dropped analytics event is
 * tolerable (ADR-036).
 */
function requeueFailed(batch: PendingEvent[]): void {
  queue.unshift(...batch);
  if (queue.length > MAX_QUEUE_SIZE) {
    const overflow = queue.length - MAX_QUEUE_SIZE;
    queue.splice(0, overflow); // drop oldest (front).
    if (import.meta.env.DEV) {
      // First-party, PII-free diagnostic — counts only, no event content.
      console.warn(
        `[analytics] queue cap ${MAX_QUEUE_SIZE} hit; dropped ${overflow} oldest event(s).`,
      );
    }
  }
}

/**
 * Build the JSON request body for a batch, stamping each event with the
 * current visit `session_id` (deduplicated — one id per visit, AC#7).
 */
function buildBody(batch: PendingEvent[]): string {
  const sessionId = ensureSessionId();
  return JSON.stringify({
    events: batch.map((e) => ({ ...e, session_id: sessionId })),
  });
}

/**
 * Flush up to MAX_BATCH_SIZE events. The trigger selects the transport:
 *   - "pagehide": `navigator.sendBeacon` when available (survives unload),
 *     ANONYMOUS (beacons carry no headers);
 *   - "size" / "timer": `fetch` with `keepalive`, attaching the in-memory
 *     Bearer token when present so the server can attribute the batch.
 *
 * Any failure re-queues the batch (AC#5/#8). Drains the stub queue first so
 * marketplace clicks reach the pipeline on the same flush.
 */
export async function flushNow(
  trigger: "size" | "timer" | "pagehide",
): Promise<void> {
  clearFlushTimer();
  drainStubQueue();
  if (queue.length === 0) {
    return;
  }
  const batch = queue.splice(0, MAX_BATCH_SIZE);
  const body = buildBody(batch);
  const url = resolveBatchUrl();

  if (trigger === "pagehide") {
    const sent = sendBeaconBatch(url, body);
    if (!sent) {
      // No beacon support (or it refused) — re-queue; a later flush retries.
      requeueFailed(batch);
    }
    return;
  }

  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const accessToken = useAuthStore.getState().accessToken;
    if (accessToken !== null) {
      headers["Authorization"] = "Bearer " + accessToken;
    }
    const res = await fetch(url, {
      method: "POST",
      headers,
      body,
      credentials: "include", // ride the refresh cookie on the same origin.
      keepalive: true, // allow the request to outlive a navigation.
    });
    if (!res.ok) {
      // 429 / 5xx / etc. — re-queue and let the next flush retry (AC#5).
      requeueFailed(batch);
    }
  } catch {
    // Network error (offline). Re-queue; events are NOT lost (AC#8).
    requeueFailed(batch);
  }
}

/**
 * Send a batch via `navigator.sendBeacon` as an `application/json` Blob.
 * Returns whether the beacon was accepted by the browser. Beacons carry no
 * request headers, so this path is anonymous by construction.
 */
function sendBeaconBatch(url: string, body: string): boolean {
  if (
    typeof navigator === "undefined" ||
    typeof navigator.sendBeacon !== "function"
  ) {
    return false;
  }
  const blob = new Blob([body], { type: "application/json" });
  return navigator.sendBeacon(url, blob);
}

/**
 * Reset all module-singleton state. FOR TESTS ONLY — clears the queue, the
 * armed timer, and the dev window handle so each test starts clean.
 */
export function __resetAnalyticsQueueForTests(): void {
  clearFlushTimer();
  queue.length = 0;
  if (typeof window !== "undefined") {
    delete window.__GARDEN_ANALYTICS_QUEUE__;
    delete window[ANALYTICS_QUEUE_KEY];
  }
  exposeDevHandle();
}

/** Read-only snapshot of the current queue length. FOR TESTS / diagnostics. */
export function analyticsQueueLength(): number {
  return queue.length;
}
